import { lazy } from 'react';
import { TrackJS } from 'trackjs';

import Actions from '@app/action';
import Store from '@app/store';

import Analytics from '@module/analytics';
import Browser from '@wetransfer/concorde-browser';
import I18n from '@module/i18n';
import Router from '@module/router';

// Static pages with code
import '@app/page/home';
import '@app/page/sign-in';
import '@app/page/plus';
import '@app/page/about';
import '@app/page/help';
import '@app/page/legal';
import '@app/page/products';

import AdvertisePage from '@app/page/advertise';
import Preview from '@app/page/transfer/preview';

import StaticPage from '@component/panel/content';
import WithAnalytics from '@component/with-analytics';

// Analytics event listeners
import { states as previewStateListeners } from '@app/analytics/preview';

import './render';

// Extend some components with Analytics
const PreviewWithAnalytics = WithAnalytics(previewStateListeners)(Preview);

const transferRoutes = ['/wallpaper/:advert_id'];

const pageRoutes = [
  // Static pages
  {
    route: '/about',
    name: 'about-page',
    component: StaticPage,
    title: I18n.t('site.titles.about'),
  },
  {
    route: '/products',
    component: StaticPage,
    name: 'products',
    title: I18n.t('site.titles.apps'),
  },
  { route: '/legal/advertising', component: StaticPage },
  { route: '/legal/disclosure', component: StaticPage },
  { route: '/legal/mobile', component: StaticPage },
  { route: '/legal/privacy', component: StaticPage },
  { route: '/legal/takedown', component: StaticPage },
  { route: '/legal/terms', component: StaticPage },
  { route: '/legal/third-party-terms', component: StaticPage },
  {
    route: '/plus',
    name: 'plus-page',
    component: StaticPage,
    title: I18n.t('site.titles.plus'),
  },
  // Advertisement pages
  {
    route: '/advertise',
    component: AdvertisePage,
    name: 'advertise-home-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/contact',
    component: AdvertisePage,
    name: 'advertise-contact-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/gallery',
    component: AdvertisePage,
    name: 'advertise-gallery-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/guidelines',
    component: AdvertisePage,
    name: 'advertise-guidelines-page',
    title: I18n.t('site.titles.advertise'),
  },
  // Payment pages
  {
    route: '/payment/completed',
    name: 'user-onboarding',
    component: lazy(() =>
      import(/* webpackChunkName: "payment" */ '@app/page/payment')
    ),
    auth: true,
  },
  {
    route: '/payment/methods',
    name: 'payment-methods',
    component: lazy(() =>
      import(/* webpackChunkName: "payment-methods" */ '@app/page/payment-methods')
    ),
    auth: true,
    payment: true,
  },
  {
    route: '/payment/update',
    name: 'payment-update',
    component: lazy(() =>
      import(/* webpackChunkName: "payment-update" */ '@app/page/payment-update')
    ),
    auth: true,
    payment: true,
  },
  {
    route: '/payment/pending',
    name: 'payment-pending',
    component: lazy(() =>
      import(/* webpackChunkName: "payment-pending" */ '@app/page/payment-pending')
    ),
    auth: true,
    payment: true,
  },
  {
    route: '/team/join/:token',
    component: lazy(() =>
      import(/* webpackChunkName: "teams-join" */ '@app/page/teams/join')
    ),
  },
];

const plusRoutes = [
  {
    route: '/help',
    component: StaticPage,
    name: 'help-section',
    title: I18n.t('site.titles.help'),
  },
  {
    route: '/release-notes',
    name: 'release-notes-page',
    component: StaticPage,
    title: I18n.t('site.titles.release_notes'),
  },
  {
    route: '/sign-in',
    component: StaticPage,
    name: 'signin-page',
    title: I18n.t('site.titles.sign_in'),
  },
  {
    route: '/forgot-password',
    component: lazy(() =>
      import(/* webpackChunkName: "forgotpassword" */ '@app/page/forgotpassword')
    ),
    title: I18n.t('site.titles.forgot_password'),
  },
  {
    route: '/ter-optout',
    component: lazy(() =>
      import(/* webpackChunkName: "unsubscribe-expiration" */ '@app/page/unsubscribeexpiration')
    ),
  },
  {
    route: '/pm-optout',
    component: lazy(() =>
      import(/* webpackChunkName: "unsubscribe-onboarding" */ '@app/page/unsubscribe-onboarding')
    ),
  },
  {
    route: '/profile',
    component: lazy(() =>
      import(/* webpackChunkName: "profile" */ '@app/page/profile')
    ),
    auth: true,
  },
  {
    route: '/account/change-password',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/your-apps',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/payment',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/payment/cancel',
    component: lazy(() =>
      import(/* webpackChunkName: "payment-cancel" */ '@app/page/payment-cancel')
    ),
    auth: true,
  },
  {
    route: '/account/billing',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/invoices',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/review-billing',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account/renew-billing',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
    payment: true,
  },
  {
    route: '/account/personal',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/account',
    component: lazy(() =>
      import(/* webpackChunkName: "account" */ '@app/page/account')
    ),
    auth: true,
  },
  {
    route: '/contacts',
    component: lazy(() =>
      import(/* webpackChunkName: "contact-list" */ '@app/page/contact/list')
    ),
    auth: true,
  },
  {
    route: '/transfers',
    component: lazy(() =>
      import(/* webpackChunkName: "transfer-list" */ '@app/page/transfer/list')
    ),
    auth: true,
  },
  {
    route: '/transfers/:transferId',
    component: lazy(() =>
      import(/* webpackChunkName: "transfer-detail" */ '@app/page/transfer/detail')
    ),
    auth: true,
  },
  {
    route: '/payment/retry',
    component: lazy(() =>
      import(/* webpackChunkName: "complete-order" */ '@app/page/complete-order')
    ),
    auth: true,
    payment: true,
  },
  {
    route: '/transfer/progress',
    component: lazy(() =>
      import(/* webpackChunkName: "transfer-progress" */ '@app/page/transfer/progress')
    ),
    auth: false,
    name: 'page-transfer-progress-details',
  },
  {
    route: '/team',
    component: lazy(() =>
      import(/* webpackChunkName: "teams" */ '@app/page/teams')
    ),
    auth: true,
  },
  {
    route: '/verify',
    name: 'verify-page',
    component: lazy(() =>
      import(/* webpackChunkName: "verify" */ '@app/page/verify')
    ),
  },
];

// if viewing non mobile version, root path is transfers
if (!Browser.isMobile) {
  transferRoutes.push('/');
  transferRoutes.push('/downloads/:id/:secret?');

  // /downloads/:id/:recipient_id/grid does NOT match this route
  transferRoutes.push('/downloads/:id/:recipient_id/:secret(\\w{6})');
  transferRoutes.push('/welcome');

  plusRoutes.push({
    route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
    component: PreviewWithAnalytics,
    auth: false,
    name: 'preview-panel',
    updateTransfer: true,
    requiresTOC: true,
    requiresTransferPassword: true,
  });
  plusRoutes.push({
    route: '/downloads/:id/:recipient_id?/:secret/grid',
    component: lazy(() =>
      import(/* webpackChunkName: "transfer-grid" */ '@app/page/transfer/grid')
    ),
    auth: false,
    name: 'grid-panel',
    updateTransfer: true,
    requiresTOC: true,
    requiresTransferPassword: true,
  });
} else {
  const Transfer = lazy(() =>
    import(/* webpackChunkName: "transfer" */ '@component/transfer')
  );

  pageRoutes.push({
    route: '/',
    component: StaticPage,
    name: 'home-mobile',
  });

  pageRoutes.push({
    route: '/upload',
    component: Transfer,
    name: 'transfer-mobile',
  });

  pageRoutes.push({ route: '/downloads/:id/:secret', component: Transfer });
  pageRoutes.push({
    route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
    component: Transfer,
  });
  pageRoutes.push({
    route: '/downloads/:id/:recipient_id?/:secret/preview',
    component: Transfer,
  });

  pageRoutes.push({
    route: '/downloads/:id/:recipient_id/:secret(\\w{6})',
    component: Transfer,
  });
  pageRoutes.push({
    route: '/downloads/:id/:recipient_id?/:secret/grid',
    component: Transfer,
    auth: false,
    name: 'grid-panel',
    updateTransfer: true,
  });
}

// Configure all available routes
const router = new Router({
  // The following routes affect the Transfer component
  transfer: transferRoutes,

  // The following routes affect the Page component, mid-width
  plus: plusRoutes,

  // The following routes affect the Page component, full-width
  page: pageRoutes,
});

// Let's see what we need to do for this new route
router.on('route', (route) => {
  const { user } = Store.getState();

  // Does this route require authentication?
  if (route.auth && !user.loggedIn) {
    router.resolve('/sign-in', false);
    Store.dispatch(Actions.Session.setRedirect(route.path));
    return;
  }

  // Record page view
  Analytics.pageView(location.pathname + location.search);

  // Update the Route in our store.
  Store.dispatch(Actions.Route.setRoute(route));
});

// Catch errors / "404" = redirect to root
router.on('error', (path) => {
  TrackJS.addMetadata('path', path);

  TrackJS.track({
    name: 'UnknownRoute',
    message: 'This route does not exist.',
    stack: 'init/routing',
  });

  router.resolve('/', false);
});

router.on('beforeroute', (complete) => {
  const { route } = Store.getState();

  // Prompt for something however you want and make sure that the complete
  // function is called when the user wants to continue.
  if (route.unSavedForm) {
    Store.dispatch(Actions.Route.setNextAction(complete));
  } else {
    complete();
  }

  // You MUST return false if you manually control when to continue
  return false;
});

// Start the router
router.start(window.location.pathname, {
  hijackLinks: true,
});
